import { useEffect, useRef, useState } from "react"
import { cn } from "@vc/lib/utils"
import { Activity, TrendingUp } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { RtpSnapshot } from "@/features/meeting/services/rtp-stats"

/**
 * What this connection is actually doing.
 *
 * Every number here used to be a literal in the caller — jitter 3, latency 35,
 * `serverIP: "34.120.45.67"`, `spatialLayer: "2/2"` — and the panel rendered
 * only for `participant.id === "p3"`, an id from demo fixtures, so nobody in a
 * real meeting ever saw it. Anyone diagnosing a bad call from it would have
 * been reading fiction, including a spatial layer that was never queried.
 *
 * The rule now: nothing is displayed that was not measured. Fields the browser
 * does not give us — the local and server IPs, a mean opinion score, the
 * connection type — are gone rather than guessed, and anything not yet sampled
 * shows a dash. An instrument that admits it does not know beats one that
 * invents an answer, which is the whole reason this rewrite exists.
 */

interface AdvancedStatsOverlayProps {
  /** Inbound for a remote tile, outbound for your own. */
  video?: RtpSnapshot | null
  audio?: RtpSnapshot | null
  className?: string
}

function fmtKbps(value?: number): string {
  if (value === undefined) return "—"
  return value >= 1000 ? `${(value / 1000).toFixed(1)} Mbps` : `${value} kbps`
}

export function AdvancedStatsOverlay({ video, audio, className }: AdvancedStatsOverlayProps) {
  const [expanded, setExpanded] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  /**
   * Collapse back to the badge on a click anywhere else.
   *
   * The panel had no dismissal at all: once opened it stayed open over the
   * video until the same menu item was found and toggled again. Collapsing —
   * rather than unmounting — keeps `useRtpStats` subscribed, so reopening shows
   * live numbers immediately instead of a panel of dashes while the next
   * sample arrives.
   *
   * `pointerdown`, not `click`: it fires before focus moves and before a menu
   * opens, so the panel is already out of the way. Capture phase so a handler
   * that stops propagation cannot keep the panel stuck open.
   */
  useEffect(() => {
    if (!expanded) return
    const onPointerDown = (e: PointerEvent) => {
      const root = rootRef.current
      if (root && e.target instanceof Node && !root.contains(e.target)) setExpanded(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false)
    }
    // `true` = capture. Also bound to the document that OWNS the panel, which
    // in a fullscreened tile is still this document — the fullscreen element is
    // in the same tree, so events reach here normally.
    document.addEventListener("pointerdown", onPointerDown, true)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [expanded])
  const t = useTranslation()

  const resolution =
    video?.frameWidth && video?.frameHeight ? `${video.frameWidth}x${video.frameHeight}` : "—"
  const fps = video?.framesPerSecond === undefined ? "—" : `${video.framesPerSecond}`
  // Loss and RTT describe the transport, so either direction can report them;
  // video is preferred only because it is the stream with the most packets to
  // be wrong about.
  const lossPct = (video ?? audio)?.fractionLost
  const jitterMs = (video ?? audio)?.jitterMs
  const rttMs = (video ?? audio)?.roundTripMs

  // What the listener actually lost, as opposed to what the network dropped.
  // Only inbound audio reports it — a sender has nothing to conceal, and video
  // conceals by holding the last frame rather than by inventing one.
  const concealPct = audio?.direction === "inbound" ? audio.concealedFraction : undefined

  const lossLabel = lossPct === undefined ? "—" : `${(lossPct * 100).toFixed(1)}%`
  const concealLabel = concealPct === undefined ? "—" : `${(concealPct * 100).toFixed(1)}%`
  const jitterLabel = jitterMs === undefined ? "—" : `${jitterMs}ms`
  const rttLabel = rttMs === undefined ? "—" : `${rttMs}ms`

  // Which rung the SFU is actually forwarding. The browser cannot answer this —
  // it only knows what it received, not what was chosen — so it comes from the
  // server. This is the honest version of the "2/2" the original overlay
  // printed as a literal, for a layer nobody had ever queried.
  const sl = (video ?? audio)?.currentSpatialLayer
  const tl = (video ?? audio)?.currentTemporalLayer
  const layerLabel = sl === undefined || sl === null ? "—" : `${sl}/${tl ?? "?"}`

  // mediasoup's own 0-10 grade of how well RTP is moving, pushed by the server.
  const score = (video ?? audio)?.score
  const scoreLabel = score === undefined ? "—" : `${score}/10`

  // Why the encoder is holding back, straight from the browser. Only outbound
  // video reports it, and 'none' is a real answer — it means measured and fine,
  // as distinct from "—", which means the browser never told us.
  const limitation = video?.qualityLimitation
  const limitedFor = video?.qualityLimitedFraction
  const limitLabel =
    limitation === undefined
      ? "—"
      : limitation === "none"
        ? "none"
        : limitedFor
          ? `${limitation} ${Math.round((limitedFor[limitation] ?? 0) * 100)}%`
          : limitation

  const videoCodec = [video?.codec, video?.mode].filter(Boolean).join(" ") || "—"

  return (
    <div
      ref={rootRef}
      className={cn(
        "group/stats pointer-events-auto relative flex flex-col rounded-lg bg-black/50 backdrop-blur-md ring-1 ring-white/10 transition-all duration-300",
        // Never taller than the space the tile actually has. The tile clips its
        // children (`overflow-hidden`), so a panel taller than the tile was cut
        // off with no way to reach the rest — which is what happens as soon as
        // the viewport shrinks (devtools open, small window, many tiles).
        // Bounding it here is what lets the inner scroll do its job.
        "max-h-full",
        expanded ? "w-[150px] sm:w-[200px] p-2 sm:p-3" : "w-[32px] p-1.5",
        className,
      )}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-blue-400 animate-pulse" />
        </div>
      </button>

      <div
        className={cn(
          // `min-h-0` is what makes a flex child actually scrollable: without it
          // the default `min-height:auto` lets this grow to its content and the
          // overflow never engages, so on a short tile the last rows were simply
          // unreachable. 260px stays as a ceiling on a tall tile so the panel
          // does not swallow the whole video.
          "grid min-h-0 gap-2 overflow-y-auto transition-opacity duration-300",
          expanded ? "mt-3 max-h-[260px] opacity-100" : "max-h-0 opacity-0",
        )}
      >
        <div className="grid grid-cols-2 gap-2 rounded-md bg-card/5 p-2">
          <StatItem label={t("advancedStats.video")} value={videoCodec} icon="video" />
          <StatItem label={t("advancedStats.audio")} value={audio?.codec ?? "—"} icon="audio" />
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-md bg-card/5 p-2">
          <StatItem label={t("advancedStats.resolution")} value={resolution} />
          <StatItem label={t("advancedStats.fps")} value={fps} />
        </div>

        <div className="space-y-1.5 rounded-md bg-card/5 p-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-white/50">{t("advancedStats.video")}</span>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-emerald-400" />
              <span className="text-xs font-semibold text-white/80">{fmtKbps(video?.kbps)}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium text-white/50">{t("advancedStats.audio")}</span>
            <div className="flex items-center gap-1">
              <TrendingUp className="h-3 w-3 text-blue-400" />
              <span className="text-xs font-semibold text-white/80">{fmtKbps(audio?.kbps)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-md bg-card/5 p-2">
          <StatItem
            label={t("advancedStats.loss")}
            value={lossLabel}
            valueClassName={
              lossPct === undefined
                ? undefined
                : lossPct > 0.01
                  ? "text-red-400"
                  : "text-emerald-400"
            }
          />
          <StatItem
            label={t("advancedStats.jitter")}
            value={jitterLabel}
            valueClassName={
              jitterMs === undefined ? undefined : jitterMs > 30 ? "text-amber-400" : "text-emerald-400"
            }
          />
          <StatItem
            label="Layer"
            value={layerLabel}
          />
          <StatItem
            label="Score"
            value={scoreLabel}
            valueClassName={
              score === undefined ? undefined : score < 5 ? "text-amber-400" : "text-emerald-400"
            }
          />
          <StatItem
            label="Limited by"
            value={limitLabel}
            valueClassName={
              limitation === undefined || limitation === "none"
                ? undefined
                : limitation === "cpu"
                  ? "text-amber-400"
                  : "text-sky-400"
            }
          />
          <StatItem
            label={t("advancedStats.latency")}
            value={rttLabel}
            valueClassName={
              rttMs === undefined
                ? undefined
                : rttMs < 100
                  ? "text-emerald-400"
                  : rttMs < 250
                    ? "text-amber-400"
                    : "text-red-400"
            }
          />
        </div>

        {/*
          Concealment — the share of audio you heard that the decoder invented.

          Its own row rather than a fourth cell in the grid above, because it is
          the only number here describing what a PERSON experienced rather than
          what the transport did. Loss can be high and this near zero, when FEC
          and NACK did their job; loss can be near zero and this high, when what
          was lost arrived too late to be worth anything.

          Shown only when the browser reports it. Chromium does, others may not,
          and a cell reading "0.0%" would be a lie about a measurement that was
          never taken.

          Under 1% is inaudible; 1-3% is where words start to smear; above that
          people ask you to repeat yourself.
        */}
        {concealPct !== undefined && (
          <div className="flex items-center justify-between rounded-md bg-card/5 p-2">
            <span className="text-[10px] font-medium text-white/50">
              {t("advancedStats.concealment")}
            </span>
            <span
              className={cn(
                "text-xs font-semibold",
                concealPct > 0.03
                  ? "text-red-400"
                  : concealPct > 0.01
                    ? "text-amber-400"
                    : "text-emerald-400",
              )}
            >
              {concealLabel}
            </span>
          </div>
        )}

        {/*
          The simulcast ladder, as it is actually being sent.
          Only your own tile has this: a receiver gets one stream and cannot see
          the layers it was chosen from. This is the panel that shows the cost of
          simulcast directly — three rows, each with its own bitrate, all leaving
          your machine at once.
        */}
        {video?.direction === "outbound" && video.layers && (
          <div className="space-y-1 rounded-md bg-card/5 p-2">
            <span className="text-[10px] font-medium text-white/50">
              {t("advancedStats.spatial")}
            </span>
            {video.layers.map((layer) => (
              <div key={layer.rid ?? "single"} className="flex items-center justify-between">
                <span className="font-mono text-[10px] text-white/60">
                  {layer.rid ?? "—"}
                  {layer.width && layer.height ? ` ${layer.width}x${layer.height}` : ""}
                </span>
                <span className="text-[11px] font-semibold text-white/80">
                  {fmtKbps(layer.kbps)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function StatItem({
  label,
  value,
  icon,
  valueClassName,
}: {
  label: string
  value: string
  icon?: "video" | "audio"
  valueClassName?: string
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-medium text-white/50">{label}</span>
      <div className="flex items-center gap-1">
        {icon && (
          <div
            className={cn(
              "h-1.5 w-1.5 shrink-0 rounded-full",
              icon === "video" ? "bg-blue-400 animate-pulse" : "bg-emerald-400 animate-pulse",
            )}
          />
        )}
        <span className={cn("truncate text-xs font-semibold text-white/80", valueClassName)}>
          {value}
        </span>
      </div>
    </div>
  )
}
